import { useCallback, useEffect, useRef } from 'react';

interface LatestRequestHandlers<T> {
  onSuccess: (data: T) => void;
  onSettled?: () => void;
}

/**
 * 处理快速切换产生的请求竞态，只允许最后一次请求更新页面状态。
 * 同时在组件卸载后使未完成请求失效，避免异步回调继续更新已卸载组件。
 * 接口错误提示由公共请求层统一处理，此处不修改当前业务数据。
 */
const useLatestRequest = () => {
  const latestRequestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      latestRequestIdRef.current += 1;
    };
  }, []);

  // 参数失效或切换业务上下文时，主动使当前未完成请求的响应失效。
  const cancelLatest = useCallback(() => {
    latestRequestIdRef.current += 1;
  }, []);

  const runLatest = useCallback(async <T>(request: () => Promise<T>, handlers: LatestRequestHandlers<T>) => {
    // 每次请求生成递增标识，响应返回时仅处理标识最新的一次。
    const requestId = ++latestRequestIdRef.current;
    const isLatest = () => mountedRef.current && requestId === latestRequestIdRef.current;

    try {
      const data = await request();
      if (isLatest()) {
        handlers.onSuccess(data);
      }
    } catch {
      // 公共接口已统一处理请求失败，此处仅保证 finally 正常收口。
    } finally {
      if (isLatest()) {
        handlers.onSettled?.();
      }
    }
  }, []);

  return { runLatest, cancelLatest };
};

export default useLatestRequest;
